import React, { useEffect, useState } from 'react';
import {
  Box, List, ListItemButton, ListItemIcon, ListItemText,
  Collapse, IconButton, TextField, Button, CircularProgress, Alert, Typography,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import { dmsApi } from '../services/dmsApi';
import useDmsPalette from '../hooks/useDmsPalette';

function FolderNode({ folder, children, selectedFolderId, onSelect, depth = 0 }) {
  const MP = useDmsPalette();
  const [open, setOpen] = useState(false);
  const isSelected = selectedFolderId === folder._id;
  const hasChildren = children.length > 0;

  return (
    <>
      <ListItemButton
        selected={isSelected}
        onClick={() => { onSelect(folder); if (hasChildren) setOpen(o => !o); }}
        sx={{
          pl: 2 + depth * 2, py: 0.5, borderRadius: 1, mx: 0.5, mb: 0.25,
          '&.Mui-selected': { bgcolor: `${MP.blue}25`, color: MP.blue },
        }}
      >
        {hasChildren ? (
          <ListItemIcon sx={{ minWidth: 24 }}>
            {open ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
          </ListItemIcon>
        ) : (
          <ListItemIcon sx={{ minWidth: 24 }}><Box sx={{ width: 20 }} /></ListItemIcon>
        )}
        <ListItemIcon sx={{ minWidth: 28, color: isSelected ? MP.blue : MP.textSecondary }}>
          {open ? <FolderOpenIcon fontSize="small" /> : <FolderIcon fontSize="small" />}
        </ListItemIcon>
        <ListItemText
          primary={folder.name}
          primaryTypographyProps={{ fontSize: '0.82rem', fontWeight: isSelected ? 600 : 400, noWrap: true }}
        />
      </ListItemButton>
      {hasChildren && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List disablePadding>
            {children.map(child => (
              <FolderNode
                key={child.folder._id}
                folder={child.folder}
                children={child.children}
                selectedFolderId={selectedFolderId}
                onSelect={onSelect}
                depth={depth + 1}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
}

function buildTree(folders) {
  const map = {};
  const roots = [];
  folders.forEach(f => { map[f._id] = { folder: f, children: [] }; });
  folders.forEach(f => {
    if (f.parentId && map[f.parentId]) {
      map[f.parentId].children.push(map[f._id]);
    } else {
      roots.push(map[f._id]);
    }
  });
  return roots;
}

export default function FolderTree({ vault, onFolderSelect, selectedFolderId }) {
  const MP = useDmsPalette();
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');

  useEffect(() => {
    if (!vault) return;
    setLoading(true);
    setError(null);
    dmsApi.listFolders({ vault })
      .then(data => setFolders(data.folders || data || []))
      .catch(err => setError(err.error || 'Failed to load folders'))
      .finally(() => setLoading(false));
  }, [vault]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    try {
      await dmsApi.createFolder({ name: newName.trim(), vault, parentId: selectedFolderId || undefined });
      setNewName('');
      setCreating(false);
      const data = await dmsApi.listFolders({ vault });
      setFolders(data.folders || data || []);
    } catch (err) {
      setError(err.error || 'Failed to create folder');
    }
  };

  const tree = buildTree(folders);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>;
  if (error) return <Alert severity="error" sx={{ m: 1 }}>{error}</Alert>;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 1 }}>
        <Typography variant="overline" sx={{ color: MP.textSecondary, fontWeight: 700, fontSize: '0.65rem' }}>
          Folders
        </Typography>
        <IconButton size="small" onClick={() => setCreating(c => !c)} sx={{ color: MP.blue }}>
          <CreateNewFolderIcon fontSize="small" />
        </IconButton>
      </Box>

      {creating && (
        <Box sx={{ display: 'flex', gap: 0.5, px: 1.5, mb: 1 }}>
          <TextField
            size="small"
            placeholder="Folder name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            autoFocus
            sx={{ flex: 1, '& .MuiInputBase-input': { fontSize: '0.8rem', py: 0.6 } }}
          />
          <Button size="small" variant="contained" onClick={handleCreate} sx={{ minWidth: 0, px: 1.2 }}>
            Add
          </Button>
        </Box>
      )}

      {tree.length === 0 ? (
        <Typography variant="caption" sx={{ px: 2, color: MP.textSecondary }}>No folders yet</Typography>
      ) : (
        <List dense disablePadding>
          {tree.map(node => (
            <FolderNode
              key={node.folder._id}
              folder={node.folder}
              children={node.children}
              selectedFolderId={selectedFolderId}
              onSelect={onFolderSelect}
            />
          ))}
        </List>
      )}
    </Box>
  );
}
